import type { SPXReferenceDefinition } from '@wix/document-services-types'
import type { CssDeclaration } from './types'

const SPX_PATTERN = /(-?\d+(?:\.\d+)?)spx/g
const REVERSE_SPX_PATTERN = /--spx\((-?\d+(?:\.\d+)?)\)/g

const SPX_PREAMBLE_LAYER_NAME = 'spx-preamble'
const SPX_FUNCTION_NAME = 'spx'

const SPX_REF_WIDTH_PROP = '--spx-ref-width'
export const DEFAULT_SPX_REF_WIDTH = 1280

const SPX_RESOLVER_TYPE_PROP = '--spx-resolver-type'
export const DEFAULT_SPX_RESOLVER_TYPE = 'scale'

export const isSpxPreambleLayer = (name: string, params?: string): boolean =>
    name === 'layer' && params === SPX_PREAMBLE_LAYER_NAME

export const getSpxPreambleLayer = (): string => `@layer ${SPX_PREAMBLE_LAYER_NAME} {
  :root {
    --scaling-factor: 100vw;
    --scrollbar-width: 0px;
    ${SPX_REF_WIDTH_PROP}: ${DEFAULT_SPX_REF_WIDTH};
    ${SPX_RESOLVER_TYPE_PROP}: ${DEFAULT_SPX_RESOLVER_TYPE};
  }

  @function --${SPX_FUNCTION_NAME}(--value <number>) returns <length> {
      result: 
        if(style(${SPX_RESOLVER_TYPE_PROP}: fixed): 
          calc(var(--value) * 1px) ; 
        else:
          max(0.5px, calc((var(--value) / var(${SPX_REF_WIDTH_PROP})) * (var(--scaling-factor) - var(--scrollbar-width))))
      )
  }
}`

export const transformSpxValue = (value: string): string =>
    value.replace(SPX_PATTERN, (_, num) => `--${SPX_FUNCTION_NAME}(${num})`)

export const restoreSpxValue = (value: string): string => value.replace(REVERSE_SPX_PATTERN, (_, num) => `${num}spx`)

export const buildSpxReferenceDeclarations = (spx: SPXReferenceDefinition | undefined): Record<string, string> => {
    if (!spx) {
        return {}
    }

    const declarations: Record<string, string> = {}

    if (spx.refWidth !== undefined && spx.refWidth !== DEFAULT_SPX_REF_WIDTH) {
        declarations[SPX_REF_WIDTH_PROP] = String(spx.refWidth)
    }

    if (spx.resolverType !== undefined && spx.resolverType !== DEFAULT_SPX_RESOLVER_TYPE) {
        declarations[SPX_RESOLVER_TYPE_PROP] = spx.resolverType
    }

    return declarations
}

interface ParsedSpxDeclarations {
    spx: SPXReferenceDefinition | undefined
    remaining: CssDeclaration[]
}

export const parseSpxReferenceDeclarations = (declarations: CssDeclaration[]): ParsedSpxDeclarations => {
    let refWidth: number | undefined
    let resolverType: string | undefined
    const remaining: CssDeclaration[] = []

    for (const declaration of declarations) {
        if (declaration.prop === SPX_REF_WIDTH_PROP) {
            refWidth = Number(declaration.value)
        } else if (declaration.prop === SPX_RESOLVER_TYPE_PROP) {
            resolverType = declaration.value
        } else {
            remaining.push(declaration)
        }
    }

    const spx =
        refWidth !== undefined || resolverType !== undefined
            ? ({ refWidth, resolverType } as SPXReferenceDefinition)
            : undefined

    return { spx, remaining }
}
